import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { 
  User as UserIcon, 
  Activity, 
  Video, 
  Calendar as CalendarIcon, 
  Clock, 
  Bell, 
  FileText,
  Check,
  Building2,
  Stethoscope,
  Loader2,
  ChevronRight,
  Search,
  ChevronDown,
  AlertCircle,
  UserCheck
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

const SearchableSelect = ({ label, options, value, onChange, placeholder, icon: Icon, required, error, disabled }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState('');
    const containerRef = useRef(null);
  
    const filteredOptions = options.filter(opt => 
      String(opt.label).toLowerCase().includes(String(search).toLowerCase())
    );
  
    const selectedOption = options.find(opt => String(opt.value) === String(value));
  
    useEffect(() => {
      const handleClickOutside = (e) => {
        if (containerRef.current && !containerRef.current.contains(e.target)) {
          setIsOpen(false);
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);
  
    return (
      <div className={`relative group ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`} ref={containerRef}>
        <label className="flex justify-between items-center px-1 mb-1.5">
          <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{label} {required && <span className="text-red-500">*</span>}</span>
          {error && <span className="text-[10px] font-black text-red-500 uppercase flex items-center gap-1 animate-pulse"><AlertCircle className="w-3 h-3" /> Required</span>}
        </label>
        <div 
          className={`w-full px-4 py-3 bg-slate-50 border rounded-2xl text-[13px] font-bold flex items-center justify-between transition-all cursor-pointer ${isOpen ? 'ring-4 ring-indigo-500/10 border-indigo-500 bg-white' : 'border-slate-100 hover:border-slate-200'}`}
          onClick={() => !disabled && setIsOpen(!isOpen)}
        >
          <div className="flex items-center gap-2.5 overflow-hidden">
            {Icon && <Icon className={`w-3.5 h-3.5 ${isOpen ? 'text-indigo-500' : 'text-slate-400'}`} />}
            <span className={`truncate ${selectedOption ? 'text-slate-800' : 'text-slate-300'}`}>
              {selectedOption ? selectedOption.label : placeholder}
            </span>
          </div>
          {!disabled && <ChevronDown className={`w-3.5 h-3.5 text-slate-300 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />}
        </div>
  
        {isOpen && (
          <div className="absolute z-[60] w-full mt-1.5 bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden animate-in fade-in zoom-in-95 duration-150">
            <div className="p-2 border-b border-slate-100 bg-slate-50/50">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400" />
                <input 
                  autoFocus
                  type="text" 
                  className="w-full pl-8 pr-3 py-2 bg-white border border-slate-200 rounded-lg text-xs font-bold focus:outline-none focus:border-indigo-400 placeholder:text-slate-300"
                  placeholder={`Search...`}
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            </div>
            <div className="max-h-48 overflow-y-auto no-scrollbar">
              {filteredOptions.length > 0 ? (
                filteredOptions.map(opt => (
                  <div 
                    key={opt.value}
                    className={`px-4 py-2.5 hover:bg-slate-50 cursor-pointer text-xs font-bold transition-colors border-b border-slate-50 last:border-0 flex items-center justify-between ${String(value) === String(opt.value) ? 'bg-indigo-50/50 text-indigo-700' : 'text-slate-600'}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      onChange(opt.value, opt.label);
                      setSearch('');
                      setIsOpen(false);
                    }}
                  >
                    {opt.label}
                    {String(value) === String(opt.value) && <Check className="w-3 h-3 text-indigo-600" />}
                  </div>
                ))
              ) : (
                <div className="px-4 py-6 text-center text-slate-300 text-[10px] font-black uppercase tracking-widest">No results</div>
              )}
            </div>
          </div>
        )}
      </div>
    );
};

const PatientBookAppointment = () => {
    const [doctors, setDoctors] = useState([]);
    const [clinics, setClinics] = useState([]);
    const [appointmentTypes, setAppointmentTypes] = useState([]);
    const [patients, setPatients] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    const storedPatientId = localStorage.getItem('patientId');
    const storedPatientName = localStorage.getItem('userName');
    const userRole = localStorage.getItem('userRole');
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    const isPatient = userRole === 'patient';

    const [formData, setFormData] = useState({
        patientId: storedPatientId || '',
        patientName: storedPatientName || '',
        doctorId: '',
        doctorName: '',
        clinicId: '',
        isVirtual: false,
        appointmentTypeId: '',
        startTime: '',
        endTime: '',
        notes: '',
        reminder: '15 min before',
        status: 'scheduled'
    });

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [typesRes, usersRes, clinicsRes] = await Promise.all([
                    axios.get(`${window.location.protocol}//${window.location.hostname}/api/appointment-types`, { headers }),
                    axios.get(`${window.location.protocol}//${window.location.hostname}/api/users`, { headers }),
                    axios.get(`${window.location.protocol}//${window.location.hostname}/api/clinics`, { headers })
                ]);

                setAppointmentTypes(typesRes.data);
                setClinics(clinicsRes.data);
                const docs = usersRes.data.filter(u => u.role === 'doctor');
                setDoctors(docs);
                
                // If not patient, fetch patient list
                if (!isPatient) {
                    const patientRes = await axios.get(`${window.location.protocol}//${window.location.hostname}/api/patients`, { headers });
                    setPatients(patientRes.data);
                }

                // Set default selections
                if (docs.length > 0) {
                    setFormData(prev => ({ ...prev, doctorId: docs[0].id, doctorName: docs[0].username }));
                }
                if (clinicsRes.data.length > 0) {
                    setFormData(prev => ({ ...prev, clinicId: clinicsRes.data[0].id }));
                }
                if (typesRes.data.length > 0) {
                    setFormData(prev => ({ ...prev, appointmentTypeId: typesRes.data[0].id }));
                }

            } catch (err) {
                toast.error('Failed to load booking data');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [isPatient]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.patientId) {
            toast.error('Please select a patient or login as a patient.');
            return;
        }

        if(!formData.startTime) {
            toast.error('Please select an appointment time');
            return;
        }

        const toastId = toast.loading('Scheduling your visit...');
        
        const submissionData = {
            ...formData,
            patientId: parseInt(formData.patientId),
            doctorId: parseInt(formData.doctorId),
            clinicId: formData.clinicId ? parseInt(formData.clinicId) : null,
            appointmentTypeId: formData.appointmentTypeId ? parseInt(formData.appointmentTypeId) : null,
            startTime: new Date(formData.startTime).toISOString(),
            endTime: new Date(formData.endTime).toISOString(),
        };

        try {
            await axios.post(`${window.location.protocol}//${window.location.hostname}/api/appointments`, submissionData, { headers });
            toast.success('Your appointment has been successfully scheduled!', { id: toastId });
            setTimeout(() => navigate('/dashboard/patient-portal/my-appointments/calendar'), 1500);
        } catch (err) {
            console.error('Booking Error:', err.response?.data || err.message);
            const errMsg = err.response?.data?.errors?.[0]?.msg || 
                           err.response?.data?.message || 
                           'Booking failed. Please try again or contact the clinic.';
            toast.error(errMsg, { id: toastId });
        }
    };

    if (loading) return (
        <div className="flex items-center justify-center min-h-[400px]">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
    );

    return (
        <div className="p-8 bg-slate-50 min-h-screen">
            <div className="max-w-4xl mx-auto">
                <header className="mb-8 flex items-center justify-between">
                    <div>
                        <h1 className="text-[32px] font-black text-slate-900 tracking-tight flex items-center gap-3">
                            <CalendarIcon className="w-10 h-10 text-indigo-600" />
                            Book Consultation
                        </h1>
                        <p className="text-slate-500 font-medium mt-2">{isPatient ? 'Self-service scheduling portal' : 'Administrative booking assistant'}</p>
                    </div>
                    <div className="bg-white px-6 py-3 rounded-2xl border border-slate-200 shadow-sm flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-black ${isPatient ? 'bg-indigo-100 text-indigo-600' : 'bg-amber-100 text-amber-600'}`}>
                            {userRole ? userRole[0].toUpperCase() : 'U'}
                        </div>
                        <div>
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Logged in with</p>
                            <p className="text-xs font-bold text-slate-700 capitalize">{userRole} access</p>
                        </div>
                    </div>
                </header>

                <div className="bg-white rounded-[40px] border border-slate-200 shadow-2xl shadow-indigo-100/50 overflow-hidden relative">
                    <div className={`absolute top-0 left-0 w-full h-2 bg-gradient-to-r ${isPatient ? 'from-indigo-500 to-purple-600' : 'from-amber-500 to-orange-600'}`} />
                    
                    <form onSubmit={handleSubmit} className="p-10 space-y-10">
                        {/* Patient Selection (Only for Staff) */}
                        {!isPatient && (
                            <div className="p-6 bg-amber-50/50 rounded-3xl border-2 border-amber-100/50 space-y-4">
                                <SearchableSelect 
                                    label="Administrative Action: Selective Patient Mapping"
                                    placeholder="Search for patient by name or MRN..."
                                    required
                                    icon={UserCheck}
                                    value={formData.patientId}
                                    options={patients.map(p => ({ value: String(p.id), label: `${p.firstName} ${p.lastName} (${p.medicalRecordNumber || 'No MRN'})` }))}
                                    onChange={(val, label) => setFormData({ ...formData, patientId: val, patientName: label.split(' (')[0] })}
                                />
                                <p className="text-[10px] font-bold text-amber-600 uppercase tracking-widest ml-1 flex items-center gap-2">
                                    <AlertCircle className="w-3 h-3" />
                                    Staff are empowered to book for any registered patient
                                </p>
                            </div>
                        )}

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8">
                            
                            <div className="space-y-8">
                                <SearchableSelect 
                                    label="Selection Logic / Type"
                                    placeholder="Choose Type..."
                                    required
                                    icon={Activity}
                                    value={formData.appointmentTypeId}
                                    options={appointmentTypes.map(t => ({ value: String(t.id), label: t.typeName }))}
                                    onChange={(val) => setFormData({ ...formData, appointmentTypeId: val })}
                                />

                                <SearchableSelect 
                                    label="Medical Practitioner"
                                    placeholder="Choose Doctor..."
                                    required
                                    icon={UserIcon}
                                    value={formData.doctorId}
                                    options={doctors.map(d => ({ value: String(d.id), label: `Dr. ${d.username}` }))}
                                    onChange={(val, label) => setFormData({ ...formData, doctorId: val, doctorName: label })}
                                />

                                <SearchableSelect 
                                    label="Clinical Facility"
                                    placeholder="Choose Unit..."
                                    required
                                    icon={Building2}
                                    value={formData.clinicId}
                                    options={clinics.map(c => ({ value: String(c.id), label: c.name }))}
                                    onChange={(val) => setFormData({ ...formData, clinicId: val })}
                                />
                            </div>

                            <div className="space-y-8">
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">Meeting Mode</label>
                                    <div className="flex gap-4">
                                        <button 
                                            type="button"
                                            onClick={() => setFormData({...formData, isVirtual: false})}
                                            className={`flex-1 p-4 rounded-2xl border-2 transition-all flex items-center justify-center gap-3 font-bold text-xs ${!formData.isVirtual ? 'border-indigo-600 bg-indigo-50 text-indigo-700 shadow-lg shadow-indigo-100' : 'border-slate-100 text-slate-400 hover:border-slate-200'}`}
                                        >
                                            <Stethoscope className="w-4 h-4" />
                                            Physical
                                        </button>
                                        <button 
                                            type="button"
                                            onClick={() => setFormData({...formData, isVirtual: true})}
                                            className={`flex-1 p-4 rounded-2xl border-2 transition-all flex items-center justify-center gap-3 font-bold text-xs ${formData.isVirtual ? 'border-purple-600 bg-purple-50 text-purple-700 shadow-lg shadow-purple-100' : 'border-slate-100 text-slate-400 hover:border-slate-200'}`}
                                        >
                                            <Video className="w-4 h-4" />
                                            Virtual
                                        </button>
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">Appointment Date & Time</label>
                                    <div className="relative group">
                                        <Clock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300 group-focus-within:text-indigo-500 transition-colors z-10" />
                                        <input 
                                            type="datetime-local"
                                            required
                                            className="w-full pl-12 pr-4 py-4 bg-slate-50 border border-slate-100 rounded-2xl text-xs font-bold text-slate-700 focus:outline-none focus:ring-4 focus:ring-indigo-500/5 focus:border-indigo-500 focus:bg-white transition-all shadow-sm"
                                            value={formData.startTime}
                                            onChange={(e) => {
                                                const start = e.target.value;
                                                const end = new Date(new Date(start).getTime() + 30 * 60000).toISOString().slice(0, 16);
                                                setFormData({...formData, startTime: start, endTime: end});
                                            }}
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">Personal Note (optional)</label>
                                    <div className="relative">
                                        <FileText className="absolute left-4 top-4 w-4 h-4 text-slate-300" />
                                        <textarea 
                                            rows="2"
                                            placeholder="Symptoms or reason for visit..."
                                            className="w-full pl-12 pr-4 py-4 bg-slate-50 border border-slate-100 rounded-2xl text-xs font-bold text-slate-700 focus:outline-none focus:border-indigo-500 transition-all resize-none shadow-sm h-[88px]"
                                            value={formData.notes}
                                            onChange={(e) => setFormData({...formData, notes: e.target.value})}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="flex items-center justify-between pt-8 border-t border-slate-100">
                            <div className="flex gap-2">
                                <div className="w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center text-slate-400">
                                    <Bell className="w-5 h-5" />
                                </div>
                                <div>
                                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Automated Reminder</p>
                                    <p className="text-xs font-bold text-slate-600">15 min before session</p>
                                </div>
                            </div>
                            <button 
                                type="submit" 
                                className={`px-12 py-5 rounded-3xl font-black text-[12px] uppercase tracking-[0.2em] shadow-2xl transition-all active:scale-[0.98] flex items-center gap-3 text-white ${isPatient ? 'bg-indigo-600 hover:bg-black shadow-indigo-200' : 'bg-amber-600 hover:bg-black shadow-amber-200'}`}
                            >
                                {isPatient ? 'Secure My Slot' : 'Confirm Patient Booking'}
                                <ChevronRight className="w-4 h-4" />
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default PatientBookAppointment;
